import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  useSendDesktopAppInstructionsMutation,
  useSendMDMEnrollInstructionsMutation,
} from '@/redux/slices/employeeApiSlice';

import AppInviteModal from './AppInviteModal';
import { Employee } from '@/types/employees';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

type AppInviteModalWrapperProps<GenericEmployee extends Employee> = {
  employees: GenericEmployee[];
  inviteVisible: boolean;
  setShowInviteModal: (show: boolean) => void;
  showCheckList?: boolean;
  fromQuickStart?: boolean;
  includeEmail?: boolean;
  includeDevice?: boolean;
  title?: string;
  description?: string;
};

const AppInviteModalWrapper = <GenericEmployee extends Employee>({
  employees,
  inviteVisible,
  includeEmail = false,
  includeDevice = true,
  setShowInviteModal,
  showCheckList = true,
  fromQuickStart,
  title,
  description,
}: AppInviteModalWrapperProps<GenericEmployee>) => {
  const { t } = useTranslation('assets');
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const [selectedEmployees, setSelectedEmployees] = useState(employees);
  const [
    sendDesktopAppInstructions,
    { isLoading: isLoadingDesktopAppInstructions },
  ] = useSendDesktopAppInstructionsMutation();
  const [sendMDMEnrollInstructions, { isLoading: isLoadingMDMInstructions }] =
    useSendMDMEnrollInstructionsMutation();
  const resetSelection = () => {
    setSelectedEmployees(employees);
  };
  const handleCancel = () => {
    setTimeout(resetSelection, 500);
    setShowInviteModal(false);
  };

  const handleSubmit = () => {
    (hasMdmEntitlement
      ? sendMDMEnrollInstructions({
          employeeIds: selectedEmployees?.map(employee => employee.id) || [],
          organizationId,
        })
      : sendDesktopAppInstructions({
          employeeIds: selectedEmployees?.map(employee => employee.id) || [],
          organizationId,
        })
    )
      .unwrap()
      .then(() => {
        showSuccessToast({
          id: 'desktop-app-invite-sent-success-toast',
          stack: true,
          message: `${t('appInviteModal.successMessagePrefix')} ${
            selectedEmployees?.length
          } ${
            selectedEmployees?.length && selectedEmployees?.length > 1
              ? t('appInviteModal.people')
              : t('appInviteModal.person')
          }`,
        });
        handleCancel();
        fromQuickStart &&
          localStorage.setItem('quickstart-app-invite-sent', 'true');
      })
      .catch(error => {
        console.error(`Unable to send desktop app invite emails for `, error);
        showErrorToast({
          id: 'desktop-app-invite-sent-error-toast',
          stack: true,
          title: t('appInviteModal.errorTitle'),
          message: t('appInviteModal.errorMessage'),
        });
      });
  };

  const instructions = hasMdmEntitlement
    ? t('appInviteModal.instructionsMDM')
    : t('appInviteModal.instructionsQS');

  const isLoading = isLoadingDesktopAppInstructions || isLoadingMDMInstructions;

  return (
    <AppInviteModal
      hide={handleCancel}
      employees={employees}
      showCheckList={showCheckList}
      selectedEmployees={selectedEmployees}
      setSelectedEmployees={setSelectedEmployees}
      isLoading={isLoading}
      visible={inviteVisible}
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      hasMdmEntitlement={hasMdmEntitlement}
      instructionsToCopy={instructions}
      includeEmail={includeEmail}
      includeDevice={includeDevice}
      title={title}
      description={description}
    />
  );
};

export default AppInviteModalWrapper;
